import {
  getDataFromLocalStorage,
  setDataToLocalStorage,
  removeDataFromLocalStorage,
} from '../../utils/helpers';
import { STREAM_UPDATE_TOKENS_STORAGE_KEY } from './constants';
import type { StreamEntitySlug } from './entities';
import type { UpdateTokensMap } from './types';

type EntityResyncVersions = Partial<Record<StreamEntitySlug, number>>;

/**
 * Entities whose stored update tokens are dropped once, so the next sync fetches
 * all of their items again. Bump an entity's number when a local schema change
 * needs data that older builds received but did not store.
 */
const ENTITY_RESYNC_VERSIONS: EntityResyncVersions = {
  // Proforma invoice schema v2 stores title, items and settings (LAE-737);
  // v3 stores the server review mark (LAE-752); v4 the payment totals (LAE-768).
  'proforma-invoices': 4,
  // Quote schema v2 stores signed_pdf_url, which locks a signed quote (LAE-749).
  'price-proposals': 1,
};

const ENTITY_RESYNC_VERSIONS_STORAGE_KEY = `${STREAM_UPDATE_TOKENS_STORAGE_KEY}_resync_versions`;

// Runs on every read, so a resync applies before the first sync uses the tokens,
// whatever the order in which the stream manager and the local database start.
const dropOutdatedEntityTokens = (tokens: UpdateTokensMap | undefined) => {
  if (typeof window === 'undefined') {
    return tokens;
  }

  const applied: EntityResyncVersions =
    getDataFromLocalStorage(ENTITY_RESYNC_VERSIONS_STORAGE_KEY) || {};
  const outdatedEntities = Object.keys(ENTITY_RESYNC_VERSIONS).filter(
    (entity) => (applied[entity] ?? 0) < ENTITY_RESYNC_VERSIONS[entity]
  );

  if (!outdatedEntities.length) {
    return tokens;
  }

  if (tokens) {
    Object.values(tokens).forEach((scopeTokens) => {
      outdatedEntities.forEach((entity) => delete scopeTokens?.[entity]);
    });
    setDataToLocalStorage(STREAM_UPDATE_TOKENS_STORAGE_KEY, tokens);
  }
  setDataToLocalStorage(ENTITY_RESYNC_VERSIONS_STORAGE_KEY, {
    ...applied,
    ...ENTITY_RESYNC_VERSIONS,
  });

  return tokens;
};

export const getStoredUpdateTokens = (): UpdateTokensMap | undefined => {
  return dropOutdatedEntityTokens(
    getDataFromLocalStorage(STREAM_UPDATE_TOKENS_STORAGE_KEY) as UpdateTokensMap | undefined
  );
};

export const setStoredUpdateTokens = (tokens: UpdateTokensMap) => {
  setDataToLocalStorage(STREAM_UPDATE_TOKENS_STORAGE_KEY, tokens);
};

export const removeStoredUpdateTokens = () => {
  removeDataFromLocalStorage(STREAM_UPDATE_TOKENS_STORAGE_KEY);
};
